/**
 * Email Reminders — one-stop dashboard for every automated email the app
 * sends. Per user request 2026-02-08: "I need to set up something on the
 * left side of the app under Apartment Lease Creator called Email
 * Reminders — a list of everything that's giving me reminders and I can
 * turn it on and off from there".
 *
 * Groups the automation catalog (backend `AUTOMATION_CATALOG`) by
 * category and delegates each row to EmailAutomationToggles which already
 * handles the PATCH round-trip and optimistic UI.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Bell, Mail, Loader2, RefreshCw, PowerOff, Power, Users, ChevronRight, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_META = {
  quick_reminders: {
    title: 'Quick Client Reminders',
    blurb: 'Weekly payment / balance and Step-3 reminders + the Friday owner digest.',
    accent: 'from-amber-500/20 via-amber-500/10 to-transparent border-amber-500/30',
    icon: '📮',
  },
  monthly_bills: {
    title: 'Monthly Bills',
    blurb: "Reminders sent for every active bill: 1 day before due, then 1 day before the 10-day grace period expires.",
    accent: 'from-emerald-500/20 via-emerald-500/10 to-transparent border-emerald-500/30',
    icon: '💵',
  },
  tradelines: {
    title: 'Tradelines',
    blurb: 'Weekly ADD digest (Mondays) and monthly REMOVE digest (1st of the month).',
    accent: 'from-fuchsia-500/20 via-fuchsia-500/10 to-transparent border-fuchsia-500/30',
    icon: '💳',
  },
  todos: {
    title: 'To-Do Reminders',
    blurb: 'Per-task email reminders. Flip on Digest Mode to coalesce them into ONE morning email.',
    accent: 'from-cyan-500/20 via-cyan-500/10 to-transparent border-cyan-500/30',
    icon: '✅',
  },
};

// 2026-02-24 — 'todos' category dropped from CATEGORY_ORDER because the
// To-Do List section was removed from the app at user request (see PRD).
// Keeping the metadata above so any legacy items in the DB still render
// with a friendly label if the server ever returns them again.
const CATEGORY_ORDER = ['quick_reminders', 'monthly_bills', 'tradelines'];

export default function EmailRemindersPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [bulking, setBulking] = useState(false);
  // 2026-02-08 — Per-client "Personal Alerts" registry. Fetched from
  // /api/personal-alerts, toggled via the existing tradeline/todo silence
  // endpoints. Shows every reminder set for a specific client so the owner
  // can silence one client without touching the global toggles.
  const [personal, setPersonal] = useState({ tradelines: [], todos: [], counts: {} });
  const [personalLoading, setPersonalLoading] = useState(true);
  const [personalSaving, setPersonalSaving] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/email-automations`);
      setItems(data.items || []);
    } catch (e) {
      console.error('[EmailRemindersPage] load failed', e);
      toast.error('Failed to load automations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Fetch the per-client alert registry.
  const loadPersonal = useCallback(async () => {
    setPersonalLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/personal-alerts`);
      setPersonal({
        tradelines: data.tradelines || [],
        todos: data.todos || [],
        counts: data.counts || {},
      });
    } catch (e) {
      console.error('[EmailRemindersPage] loadPersonal failed', e);
    } finally {
      setPersonalLoading(false);
    }
  }, []);

  useEffect(() => { loadPersonal(); }, [loadPersonal]);

  // Flip silence on a single tradeline / todo. Optimistic UI, rollback on error.
  const flipPersonal = async (kind, id, next, label) => {
    const skey = `${kind}:${id}`;
    setPersonalSaving((s) => ({ ...s, [skey]: true }));
    // Optimistic
    setPersonal((prev) => ({
      ...prev,
      [kind]: prev[kind].map((r) => (r.id === id ? { ...r, reminders_silenced: next } : r)),
    }));
    try {
      const url = kind === 'tradelines'
        ? `${API}/api/tradeline-world/active/${id}/silence`
        : `${API}/api/todos/${id}/silence`;
      await axios.patch(url, null, { params: { silence: next } });
      toast.success(next
        ? `Silenced reminders for ${label}`
        : `Re-enabled reminders for ${label}`);
    } catch (e) {
      // Rollback
      setPersonal((prev) => ({
        ...prev,
        [kind]: prev[kind].map((r) => (r.id === id ? { ...r, reminders_silenced: !next } : r)),
      }));
      const detail = e?.response?.data?.detail || e?.message || 'network error';
      toast.error(`Couldn't toggle — ${detail}`);
    } finally {
      setPersonalSaving((s) => ({ ...s, [skey]: false }));
    }
  };

  const flip = async (key, next) => {
    setSaving((s) => ({ ...s, [key]: true }));
    // Optimistic update so the pill flips instantly even on slow connections.
    setItems((prev) => prev.map((i) => (i.key === key ? { ...i, enabled: next } : i)));
    try {
      const { data } = await axios.patch(
        `${API}/api/email-automations/${key}`,
        { enabled: next },
        { timeout: 12000 },
      );
      // If the server echoed back a full automation doc, replace with the
      // canonical version. If not (older backend / weird payload), just
      // keep the optimistic flip we already applied.
      const server = (data && (data.automation || data.item)) || null;
      if (server && server.key) {
        setItems((prev) => prev.map((i) => (i.key === key ? server : i)));
      }
      toast.success(`${server?.label || 'Reminder'} is now ${next ? 'ON' : 'OFF'}`);
    } catch (e) {
      // Roll back the optimistic flip so the switch matches reality.
      setItems((prev) => prev.map((i) => (i.key === key ? { ...i, enabled: !next } : i)));
      const detail = e?.response?.data?.detail || e?.message || 'network error';
      console.error('[EmailRemindersPage] flip failed', e);
      toast.error(`Couldn't toggle "${key}" — ${detail}`);
    } finally {
      setSaving((s) => ({ ...s, [key]: false }));
    }
  };

  // Bulk actions per user request — "I'm still getting over 15 emails every
  // morning" — give them a single button to shut off every stream at once,
  // and another to turn them all back on when they're ready.
  const bulkSet = async (nextState) => {
    if (!window.confirm(
      nextState
        ? 'Turn ON every automated email stream? Clients will start receiving reminders again.'
        : 'Turn OFF every automated email stream? No reminder emails will go out until you flip them back on.'
    )) return;
    setBulking(true);
    try {
      const requests = items
        .filter((i) => i.enabled !== nextState)
        .map((i) => axios.patch(`${API}/api/email-automations/${i.key}`, { enabled: nextState }));
      await Promise.all(requests);
      toast.success(`All email reminders ${nextState ? 'enabled' : 'silenced'}`);
      await load();
    } catch (e) {
      console.error('[EmailRemindersPage] bulk failed', e);
      toast.error('One or more updates failed — refreshing…');
      await load();
    } finally {
      setBulking(false);
    }
  };

  const grouped = CATEGORY_ORDER
    .map((cat) => ({ cat, meta: CATEGORY_META[cat], rows: items.filter((i) => i.category === cat) }))
    .filter((g) => g.rows.length > 0);

  const enabledCount = items.filter((i) => i.enabled).length;

  return (
    <div className="min-h-screen bg-zinc-950 text-white" data-testid="email-reminders-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {/* Back to Dashboard — user 2026-02-08: "we need a back button". */}
        <div className="mb-4">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-zinc-400 hover:text-emerald-300 transition"
            data-testid="er-back-btn"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Back to Dashboard
          </Link>
        </div>
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-emerald-300/80 mb-2">
            <Bell className="w-3.5 h-3.5" /> Email Reminders
          </div>
          <h1 className="text-3xl sm:text-4xl font-black text-white leading-tight">
            One place to silence every reminder
          </h1>
          <p className="text-sm text-zinc-400 mt-2 max-w-2xl leading-relaxed">
            Every automated email the app sends is listed below, grouped by kind. Flip any switch off and
            that specific stream stops firing — the scheduler checks the flag every time it wakes up.
          </p>
        </div>

        {/* Summary + bulk actions */}
        <div className="mb-6 rounded-xl border border-zinc-800 bg-zinc-900/60 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <Mail className="w-5 h-5 text-emerald-400" />
            <div>
              <div className="text-sm font-semibold text-white" data-testid="er-summary">
                {loading ? 'Loading…' : `${enabledCount} of ${items.length} streams are ON`}
              </div>
              <div className="text-[11px] text-zinc-500">
                All times are Pacific — the app&apos;s business timezone.
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { load(); loadPersonal(); }}
              disabled={loading || personalLoading}
              className="p-2 rounded-md bg-zinc-800 hover:bg-zinc-700 text-zinc-300 disabled:opacity-40"
              title="Reload"
              data-testid="er-refresh"
            >
              <RefreshCw className={`w-4 h-4 ${(loading || personalLoading) ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={() => bulkSet(false)}
              disabled={bulking || loading}
              className="px-3 py-2 rounded-md bg-red-500/15 hover:bg-red-500/25 text-red-300 border border-red-500/30 text-xs font-bold uppercase tracking-wider disabled:opacity-40 flex items-center gap-1.5"
              data-testid="er-silence-all"
            >
              <PowerOff className="w-3.5 h-3.5" /> Silence all
            </button>
            <button
              onClick={() => bulkSet(true)}
              disabled={bulking || loading}
              className="px-3 py-2 rounded-md bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 border border-emerald-500/30 text-xs font-bold uppercase tracking-wider disabled:opacity-40 flex items-center gap-1.5"
              data-testid="er-enable-all"
            >
              <Power className="w-3.5 h-3.5" /> Enable all
            </button>
          </div>
        </div>

        {/* Loading state */}
        {loading && (
          <div className="flex items-center gap-2 text-zinc-400 text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading reminders…
          </div>
        )}

        {/* Grouped list */}
        {!loading && grouped.map(({ cat, meta, rows }) => (
          <section key={cat} className="mb-6" data-testid={`er-category-${cat}`}>
            <div className={`rounded-xl border bg-gradient-to-br ${meta.accent} p-4 mb-3`}>
              <div className="flex items-center gap-2 text-sm font-black uppercase tracking-wider text-white">
                <span className="text-lg">{meta.icon}</span> {meta.title}
              </div>
              <p className="text-xs text-zinc-300/80 mt-1">{meta.blurb}</p>
            </div>
            <div className="space-y-2">
              {rows.map((it) => (
                <div
                  key={it.key}
                  className="flex items-start gap-3 p-3 rounded-lg bg-zinc-900 border border-zinc-800 hover:border-emerald-500/30 transition"
                  data-testid={`er-item-${it.key}`}
                >
                  <button
                    type="button"
                    onClick={() => flip(it.key, !it.enabled)}
                    disabled={!!saving[it.key]}
                    className={`shrink-0 relative inline-flex h-6 w-11 items-center rounded-full transition ${
                      it.enabled ? 'bg-emerald-500' : 'bg-zinc-600'
                    } ${saving[it.key] ? 'opacity-50 cursor-wait' : 'cursor-pointer'}`}
                    data-testid={`er-toggle-${it.key}`}
                    aria-label={`${it.enabled ? 'Disable' : 'Enable'} ${it.label}`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                        it.enabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-white">{it.label}</span>
                      <span
                        className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold uppercase tracking-wider ${
                          it.enabled
                            ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                            : 'bg-zinc-700/60 text-zinc-400 border border-zinc-600/50'
                        }`}
                      >
                        {it.enabled ? 'ON' : 'OFF'}
                      </span>
                    </div>
                    <div className="text-[11px] text-zinc-500 mt-0.5">📅 {it.schedule}</div>
                    <div className="text-xs text-zinc-300/80 mt-1.5 leading-snug">
                      {it.description}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))}

        {/* Empty state (shouldn't happen post-seed but guard anyway) */}
        {!loading && grouped.length === 0 && (
          <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-6 text-center text-zinc-500 text-sm">
            No automations configured yet.
          </div>
        )}

        {/* 2026-02-08 — Per-client "Personal Alerts" section per user
            request. Lists every reminder that's set for a specific client
            (tradelines + to-dos) so the owner can silence one client
            without disturbing the rest. */}
        <section className="mt-10 pt-6 border-t border-zinc-800" data-testid="er-personal-alerts">
          <div className="rounded-xl border border-sky-500/40 bg-gradient-to-br from-sky-500/15 via-sky-500/5 to-transparent p-4 mb-4">
            <div className="flex items-center gap-2 text-sm font-black uppercase tracking-wider text-white">
              <Users className="w-4 h-4 text-sky-300" /> Personal Alerts (per client)
            </div>
            <p className="text-xs text-zinc-300/80 mt-1">
              Every reminder currently set for a specific client. Turning one off silences JUST that client&apos;s
              emails — the rest of the app keeps sending reminders as normal.
            </p>
          </div>

          {personalLoading && (
            <div className="flex items-center gap-2 text-zinc-400 text-sm mb-4">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading personal alerts…
            </div>
          )}

          {!personalLoading && (
            <>
              {/* Tradelines */}
              <div className="mb-6" data-testid="er-personal-tradelines">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs uppercase tracking-wider text-fuchsia-300 font-bold">
                    💳 Tradelines — Active Client Reminders
                  </div>
                  <span className="text-[10px] text-zinc-500">
                    {personal.counts?.tradelines_silenced || 0} silenced of {personal.counts?.tradelines_total || 0}
                  </span>
                </div>
                {personal.tradelines.length === 0 ? (
                  <div className="rounded-md border border-zinc-800 bg-zinc-900/40 p-4 text-center text-zinc-500 text-xs">
                    No active tradelines set up yet.
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    {personal.tradelines.map((r) => {
                      const skey = `tradelines:${r.id}`;
                      const busy = !!personalSaving[skey];
                      return (
                        <div
                          key={r.id}
                          className="flex items-center gap-3 px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-800 hover:border-fuchsia-500/40 transition"
                          data-testid={`er-personal-tl-${r.id}`}
                        >
                          <button
                            type="button"
                            onClick={() => flipPersonal('tradelines', r.id, !r.reminders_silenced, r.client_name)}
                            disabled={busy}
                            className={`shrink-0 relative inline-flex h-5 w-9 items-center rounded-full transition ${
                              !r.reminders_silenced ? 'bg-emerald-500' : 'bg-zinc-600'
                            } ${busy ? 'opacity-50 cursor-wait' : 'cursor-pointer'}`}
                            aria-label={r.reminders_silenced ? `Enable reminders for ${r.client_name}` : `Silence reminders for ${r.client_name}`}
                            data-testid={`er-personal-tl-toggle-${r.id}`}
                          >
                            <span
                              className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition ${
                                !r.reminders_silenced ? 'translate-x-5' : 'translate-x-0.5'
                              }`}
                            />
                          </button>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-white truncate">{r.client_name}</div>
                            <div className="text-[11px] text-zinc-500 flex items-center gap-1 flex-wrap">
                              <span>{r.card_name || 'Card'}</span>
                              <ChevronRight className="w-3 h-3 opacity-50" />
                              <span>added {r.add_date || '(no date)'}</span>
                            </div>
                          </div>
                          <span
                            className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold uppercase tracking-wider ${
                              !r.reminders_silenced
                                ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                                : 'bg-zinc-700/60 text-zinc-400 border border-zinc-600/50'
                            }`}
                          >
                            {!r.reminders_silenced ? 'ON' : 'MUTED'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* 2026-02-24 — To-Do Reminders panel removed at user request.
                  The daily 9AM PST emails couldn't be reliably muted so the
                  entire To-Do section (dashboard tile, /todo route, scheduler
                  job, personal-alerts panel) was pulled from the app.
                  Backend endpoints stay behind for data safety only. */}
            </>
          )}
        </section>

        <div className="mt-8 rounded-lg border border-zinc-800 bg-zinc-900/40 p-4 text-[11px] text-zinc-500 leading-relaxed">
          <strong className="text-zinc-300">How this works:</strong> the scheduler checks each toggle
          right before it would fire. Flipping OFF stops future emails immediately (no restart needed).
          Flipping ON resumes them at the next scheduled run. Per-client silence lives on the
          individual client (see the bell button next to their name in Tradeline World).
        </div>
      </div>
    </div>
  );
}
